import { PopoverProps } from '@mui/material';
import { Theme } from '@mui/material/styles';
import { SystemStyleObject } from '@mui/system';
import { radiusHalf } from 'utility/borderRadius';
import { gridClasses } from '@mui/x-data-grid-pro';
import { bloxValues } from 'themes/constants';
import { getColorMix, getPrimaryBgColor, getSolidThemeGray } from 'utility/styles';
import { summaryCardHeight } from './components/fields/generic/SummaryCard';
import { SearchContextType } from './types';
import { isNumber } from 'remeda';

// ------------------------------------------------
// Styles for Search Pages & Popovers
// ------------------------------------------------

export const searchIconFontSize = 24;
export const spaceVal = 1.5; // 1.5 mui = 12px for margin, gaps, etc around the grid and in the cards.

export const resetFiltersButtonSx = (theme: Theme): SystemStyleObject => ({
  fontSize: bloxValues.smallFontSizeRem,
  fontWeight: bloxValues.fontWeightRegular,
  color: theme.palette.text.secondary,
  height: '34px',
});

export const getDefaultIconSx = (theme: Theme, color?: string): SystemStyleObject => ({
  m: 0,
  p: 0,
  fontSize: bloxValues.iconSizeRegular,
  borderRadius: theme.shape.borderRadius,
  color: color || theme.palette.text.secondary,
});

export const getBadgeIconSx = (theme: Theme): SystemStyleObject => ({
  '& .MuiBadge-badge': {
    fontSize: bloxValues.tinyFontSizeRem,
  },
});

export const viewTypeIconButtonSx = (theme: Theme): SystemStyleObject => ({
  m: 0,
  p: 0.5,
  '& svg': {
    fontSize: bloxValues.iconSizeRegular,
  },
});

export const getTextInputBorderContainerSx = (theme: Theme): SystemStyleObject => {
  /**
   * When the search TextField child is focused
   * provide styling to indicate focused state.
   * progressive enhancement: only applied if browsers supports :has()
   * "@supports" also prevents jsdom from crashing on :has() in tests.
   * https://developer.mozilla.org/en-US/docs/Web/CSS/Reference/Selectors/:has
   **/

  return {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    flexGrow: 1,
    p: '2px',
    gap: 0.5,
    width: '100%',
    minWidth: 135,
    maxWidth: 600,
    minHeight: 34,
    borderRadius: bloxValues.halfBorderRadius,
    outlineStyle: 'solid',
    outlineWidth: '1px',
    outlineColor: theme.palette.divider,
    transition: theme.transitions.create(['outline-color', 'outline-width'], {
      duration: 60,
      easing: theme.transitions.easing.easeIn,
    }),
    '@supports selector(:has(*))': {
      '&:has(input[type="text"]:focus)': {
        outlineWidth: '2px',
        outlineColor: theme.palette.primary.main,
      },
    },
  };
};

export const getSearchTextfieldSx = (
  theme?: Theme,
  disabled?: boolean,
): SystemStyleObject => ({
  p: 0,
  m: 0,
  fontSize: bloxValues.tinyFontSizeRem,
  borderStyle: 'none',
  width: '100%',
  '& .MuiOutlinedInput-root': {
    fontSize: bloxValues.tinyFontSizeRem,
    px: 0.5,
    '& fieldset': {
      fontSize: bloxValues.tinyFontSizeRem,
      borderStyle: 'none',
      paddingBlock: 0,
    },
    '&.Mui-focused fieldset': {
      borderStyle: 'none',
      p: 0,
    },
    '& .MuiInputBase-input.MuiOutlinedInput-input': {
      p: 0,
      '&::placeholder': {
        fontStyle: !disabled ? 'italic' : 'normal',
      },
    },
  },
});

// IconButton Styles for Advanced Filters Button
export const getAdvFiltersBtnSx = (
  theme: Theme,
  hasFilters: boolean,
): SystemStyleObject => ({
  p: 0.5,
  m: 0,
  fontSize: 24,
  bgcolor: hasFilters ? getPrimaryBgColor(theme) : undefined,
  '&:hover': {
    bgcolor: hasFilters ? getPrimaryBgColor(theme, true) : undefined,
  },
});

export const getResultsSx = (theme: Theme): SystemStyleObject => ({
  fontSize: bloxValues.tinyFontSizeRem,
  color: theme.palette.text.secondary,
});

export const getIconButtonSx = (theme: Theme): SystemStyleObject => ({
  p: 0.5,
  m: 0,
  fontSize: 24,
  transition: theme.transitions.create(['background-color', 'box-shadow']),
});

// Advanced Search Input Popover Styles
export const getAdvSearchPopoverPaperSx = (theme: Theme): SystemStyleObject => ({
  display: 'grid',
  gridTemplateRows: 'auto 1fr auto',
  gridTemplateAreas: `'advancedheader' 'advancedinputs' 'advancedfooter'`,
  overflow: 'hidden', // no scroll
  container: 'advancedpopover / normal',
  minWidth: 400,
  maxHeight: '65vh', // expanded kpi bar is a bit too tall for 75% of vh, setting slightly lower
  [theme.breakpoints.down('sm')]: {
    // fullscreen mobile overrides with slight safezone
    width: '95vw',
    height: 'auto',
    maxWidth: '95vw',
    maxHeight: '95vh',
    // lower any minimums
    minWidth: '200px',
    minHeight: '200px',
  },
});

export const advSearchElevation: PopoverProps['elevation'] = 3;

export const getAdvSearchThreshold = (theme: Theme): PopoverProps['marginThreshold'] => {
  const minimums = theme.breakpoints.down('sm') ? 10 : 20; // px
  return minimums;
};

export const advSearchAnchorOrigin: PopoverProps['anchorOrigin'] = {
  vertical: 'bottom',
  horizontal: 'center',
};

export const advSearchTransformOrigin: PopoverProps['transformOrigin'] = {
  vertical: 'top',
  horizontal: 'center',
};

export const getAdvancedPopoverRootSx = (theme: Theme): SystemStyleObject => ({
  backgroundColor: '#00000040', // 25% opacity background overlay dimming
});

export const getAdvancedPopoverTitleSx = (theme: Theme): SystemStyleObject => ({
  fontSize: bloxValues.smallFontSizeRem,
  fontWeight: 500,
});

export const getAdvancedPopoverHeaderAreaSx = (theme: Theme): SystemStyleObject => ({
  background: getSolidThemeGray(theme),
  borderBottom: `1px solid ${theme.palette.divider}`,
  p: 1,
});

export const getAdvancedPopoverInputsAreaSx = (theme: Theme): SystemStyleObject => ({
  gridArea: 'advancedinputs',
  display: 'grid',
  overflow: 'auto', // scroll area
  gridTemplateColumns: '3fr 7fr', // 30/70 split
  alignItems: 'center',
  bgcolor: theme.palette.background.paper,
  gap: 2,
  m: 1, // margin provides spacing without gap on sticky tabs
});

export const getAdvancedPopoverFooterAreaSx = (theme: Theme): SystemStyleObject => ({
  gridArea: 'advancedfooter',
  overflow: 'hidden', // no scroll
  background: getSolidThemeGray(theme),
  borderTop: `1px solid ${theme.palette.divider}`,
  gap: 2,
  p: 1,
});

export const getClearStagedFiltersButtonSx = (theme: Theme): SystemStyleObject => ({
  borderColor: theme.palette.text.secondary,
  color: theme.palette.text.secondary,
  bgcolor: theme.palette.background.paper,
  '&:hover': {
    borderColor: theme.palette.text.primary,
    color: theme.palette.text.primary,
    bgcolor: theme.palette.background.paper,
    boxShadow: theme.shadows[2],
  },
});

/**
 * Grid Actions Col Width
 * (eg: wire search - add to site)
 */
export const gridActionsColWidth = 30;

// + 12px is padding between rows as cant directly on row without
// odd things happening with the grid virtualization per cell
// mui grid seemingly needs a precalculated value for heights
// so we cant use auto, 100%, etc without it breaking cells
const summaryCellHeight = summaryCardHeight + 12;
const listCellHeight = 38;

type ViewType = Partial<SearchContextType>['view'] | undefined; // 'list' | 'summary'

type GetRowHeightInfoArgs = {
  view?: ViewType;
  resource?: string;
};

/**
 * Number for pixels, or 'auto' height calculation.
 *
 * Which is effectively this type from MUI (extracted):
 * ReturnType<NonNullable<DataGridProProps['getRowHeight']>>;
 * number | 'auto' | null | undefined
 */
type GetRowHeightInfoResult = number | 'auto';

const AUTO = 'auto';

export function getRowHeightInfo({
  view,
  resource,
}: GetRowHeightInfoArgs): GetRowHeightInfoResult {
  // default row height calculations
  if (view === 'list') {
    if (resource === 'collections') {
      return 67;
    }
    return listCellHeight;
  }
  if (view === 'summary') {
    return summaryCellHeight;
  }
  // Default to auto, warn dev if unhandled
  console.warn(`[getRowHeightInfo]: Unhandled "${view}" type.`);
  return AUTO;
}

type GetDatagridStylesInfoArgs = {
  theme: Theme;
  view?: ViewType;
  resource?: string;
};

type GetDatagridStylesInfoReturn = {
  sxStyles: SystemStyleObject;
  rowHeight: GetRowHeightInfoResult;
};

export function getDatagridStylesInfo({
  theme,
  view,
  resource,
}: GetDatagridStylesInfoArgs): GetDatagridStylesInfoReturn {
  // Calculate cell height based on view and resource
  const cellHeight = getRowHeightInfo({ view, resource });

  // Convert to px string if number, else keep 'auto' (or a css string)
  const isValidNumHeight = isNumber(cellHeight) && Number.isFinite(cellHeight);
  const cellHeightCss = isValidNumHeight ? `${cellHeight}px` : cellHeight;

  const isDarkMode = theme.palette.mode === 'dark';
  const hoverMixPct = isDarkMode ? 25 : 15;
  const activeMixPct = isDarkMode ? 35 : 25;
  const selectedBgColor = isDarkMode ? theme.palette.grey[700] : theme.palette.grey[200];
  const baseRowColor = isDarkMode
    ? theme.palette.common.black
    : theme.palette.common.white;
  const oddRowColor = theme.palette.background.paper;
  const headerBgColor = isDarkMode ? theme.palette.grey[900] : theme.palette.grey[50];
  const pinnedHeaderBgColor = isDarkMode
    ? theme.palette.grey[800]
    : theme.palette.grey[100];

  const hoverBgColor = getColorMix(
    theme.palette.primary.light,
    hoverMixPct,
    theme.palette.background.default,
    'srgb',
  );

  const activeBgColor = getColorMix(
    theme.palette.primary.light,
    activeMixPct,
    theme.palette.background.default,
    'srgb',
  );

  const dgStyles: SystemStyleObject = {
    '--unstable_DataGrid-radius': '0px',
    fontSize: bloxValues.smallFontSizeRem,
    border: 'none',
    borderRadius: '0px',
    overflow: 'auto',
    height: '100%',
    width: '100%',
    minHeight: '100%',
    minWidth: '100%',

    [`& .${gridClasses.root}`]: {
      border: 'none',
      borderRadius: '0px',
      overflow: 'auto',
      height: '100%',
      width: '100%',
      minHeight: '100%',
      minWidth: '100%',
    },

    '& .MuiTypography-root': {
      fontSize: bloxValues.smallFontSizeRem,
      lineHeight: 'normal',
      whiteSpace: 'normal',
      overflowWrap: 'break-word',
      wordBreak: 'break-all',
      textWrap: 'nowrap',
      hyphens: 'none',
    },

    // Cell styling
    [`& .${gridClasses.cell}`]: {
      borderTop: 'none',
      borderBottom: `1px solid ${theme.palette.action.hover}`,
      // SummaryCard.tsx minheight for border bottom to not break cells
      // in summary view as the grid virtualizes cells/rows heights
      // as well as define heights for list view to reduce jank between swap
      height: cellHeightCss,
      minHeight: cellHeightCss,
      display: 'flex',
      alignItems: 'center',
      flexWrap: 'nowrap',
      lineHeight: 'normal',
      whiteSpace: 'normal',
      overflowWrap: 'break-word',
      wordBreak: 'break-all',
      textWrap: 'nowrap',
      hyphens: 'none',
      '&:focus-within': {
        outline: 'none',
      },
    },

    // Filler styles
    [`& .${gridClasses.filler}`]: {
      backgroundColor: headerBgColor,
    },
    [`& .${gridClasses.filler}, & .${gridClasses['filler--pinnedLeft']}, & .${gridClasses['filler--pinnedRight']}`]:
      {
        backgroundColor: headerBgColor,
      },
    [`& .${gridClasses.scrollbarFiller}`]: {
      backgroundColor: headerBgColor,
    },
    // Seems to have been removed in MUI v8
    // [`& .${gridClasses['scrollbarFiller--header']}`]: {
    //   borderBottom: `1px solid ${theme.palette.divider}`,
    //   backgroundColor: headerBgColor,
    // },
    [`& .${gridClasses['container--top']}`]: {
      borderBottom: `1px solid ${theme.palette.divider}`,
    },

    // Checkbox styling
    [`& .${gridClasses.cellCheckbox}, & .${gridClasses.columnHeaderCheckbox}`]: {
      '& .MuiSvgIcon-root': {
        width: 20,
        height: 20,
      },
    },

    // Header styles
    [`& .${gridClasses.columnHeader}`]: {
      borderRight: `1px solid ${theme.palette.action.hover}`,
      boxShadow: `0 1px 0 ${theme.palette.divider}`,
      backgroundColor: headerBgColor,
    },

    // Pinned column styles
    [`& .${gridClasses['columnHeader--pinnedLeft']}`]: {
      backgroundColor: pinnedHeaderBgColor,
      borderBottom: `1px solid ${theme.palette.divider}`,
      borderRight: `1px solid ${theme.palette.divider}`,
    },
    [`& .${gridClasses['columnHeader--pinnedRight']}`]: {
      backgroundColor: pinnedHeaderBgColor,
      borderBottom: `1px solid ${theme.palette.divider}`,
      borderLeft: `1px solid ${theme.palette.divider}`,
    },
    [`& .${gridClasses['cell--pinnedLeft']}`]: {
      borderRight: `1px solid ${theme.palette.divider}`,
      boxShadow: `0 0 8px ${theme.palette.divider}`,
      clipPath: 'inset(0px -8px 0px 0px)',
    },
    [`& .${gridClasses['cell--pinnedRight']}`]: {
      borderLeft: `1px solid ${theme.palette.divider}`,
      boxShadow: `0 0 8px ${theme.palette.divider}`,
      clipPath: 'inset(0px 0px 0px -8px)',
    },

    // MUI has some virtualScrollerContent classes
    // that we need to override for rows/pinned/etc
    // to get the background color to show up correctly on hover/active/select states
    // [`& .${gridClasses['cell--pinnedLeft']}, & .${gridClasses['cell--pinnedRight']}`]:
    // { backgroundColor: `${activeBgColor} !important` }, // can force with !important here
    // as targeting the virtualScrollerContent class can be tricky
    // since it seems MUI is applying the virtual class above ours
    // .MuiDataGrid-virtualScrollerContent .MuiDataGrid-row:hover .MuiDataGrid-cell--pinnedLeft
    // or we can wrap the entire gridClasses.row in the virtualScrollerContent styles
    [`& .${gridClasses.virtualScrollerContent}`]: {
      // row styles
      [`& .${gridClasses.row}`]: {
        backgroundColor: baseRowColor,
        alignItems: 'center',
        cursor: 'pointer',

        // pinned col cell
        [`& .${gridClasses['cell--pinnedLeft']}, & .${gridClasses['cell--pinnedRight']}`]:
          {
            backgroundColor: headerBgColor,
          },

        // odd
        '&.odd': {
          backgroundColor: oddRowColor,
        },

        // disabled
        '&.disabled': {
          opacity: 0.7,
          color: theme.palette.text.secondary,
          cursor: 'default',
        },

        // underlined (rare, for dynamic collection)
        '&.underlined': {
          borderBottom: `2px solid ${theme.palette.primary.main}`,
        },

        // hover state (including pinned cells)
        '&:hover': {
          backgroundColor: hoverBgColor,
          [`& .${gridClasses['cell--pinnedLeft']}, & .${gridClasses['cell--pinnedRight']}`]:
            {
              backgroundColor: `${hoverBgColor} `,
            },
        },

        // active state (including pinned cells)
        '&:active': {
          backgroundColor: activeBgColor,
          [`& .${gridClasses['cell--pinnedLeft']}, & .${gridClasses['cell--pinnedRight']}`]:
            {
              backgroundColor: `${activeBgColor}`,
            },
        },

        // selected states
        '&.Mui-selected': {
          backgroundColor: selectedBgColor,
          [`& .${gridClasses['cell--pinnedLeft']}, & .${gridClasses['cell--pinnedRight']}`]:
            {
              backgroundColor: `${selectedBgColor}`,
            },
        },

        // selected + hover state
        '&.Mui-selected:hover': {
          backgroundColor: hoverBgColor,
          [`& .${gridClasses['cell--pinnedLeft']}, & .${gridClasses['cell--pinnedRight']}`]:
            {
              backgroundColor: `${hoverBgColor}`,
            },
        },

        // selected + active state
        '&.Mui-selected:active': {
          backgroundColor: activeBgColor,
          [`& .${gridClasses['cell--pinnedLeft']}, & .${gridClasses['cell--pinnedRight']}`]:
            {
              backgroundColor: `${activeBgColor}`,
            },
        },
      },
    },
  };

  const result: GetDatagridStylesInfoReturn = {
    sxStyles: dgStyles,
    rowHeight: cellHeight,
  };

  return result;
}

export const searchGridStyles = (theme: Theme): SystemStyleObject => ({
  container: 'searchgrid / inline-size',
  display: 'grid',
  gridTemplateAreas: `
          'searchinputs'
          'searchresults'
        `,
  gridTemplateRows: 'auto 1fr',
  gridTemplateColumns: '1fr',
  overflow: 'hidden',
  height: '100%',
  width: '100%',
  minHeight: '100%',
  minWidth: '100%',
});

// Editorial Search Results Wrapper (MUI DataGrid + Card View)
export const searchResultsWrapperSx = (
  theme: Theme,
  view?: string,
): SystemStyleObject => ({
  // Grid container
  gridArea: 'searchResults',
  container: 'searchResults / inline-size',
  overflow: 'auto',

  // Area Sizing
  height: '100%',
  width: '100%',
  minHeight: '100%',
  minWidth: '100%',

  // Typography
  fontSize: 14,
  lineHeight: 'normal',
  whiteSpace: 'normal',
  overflowWrap: 'break-word',
  wordBreak: 'break-all',
  textWrap: 'nowrap',
  hyphens: 'none',
});

export const skeletonBoxSx = (theme: Theme): SystemStyleObject => ({
  p: spaceVal,
  my: spaceVal,
  borderRadius: radiusHalf(theme),
});

export const infoTypographySx = (theme: Theme): SystemStyleObject => ({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  p: spaceVal,
  my: spaceVal,
  color: theme.palette.text.secondary,
  backgroundColor: theme.palette.action.hover,
  border: `1px solid ${theme.palette.action.hover}`,
  borderRadius: radiusHalf(theme),
  fontWeight: 400,
  fontSize: 14,
  mx: 2,
});

// --------------------
// Popovers
// --------------------
export const getPopoverGridInputLabelSx = (theme: Theme) => ({
  fontSize: bloxValues.tinyFontSizeRem,
  fontWeight: 500,
  color: theme.palette.text.primary,
});
